import React, { useContext, useEffect, useRef, useState } from "react";
import { Formik, Form, Field } from "formik";
import {
  Box,
  DialogTitle,
  Grid,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { projectTypeOptions } from "ts-interfaces";
import AuthModal from "../../studioxr/auth/AuthModal";
import { ActionButton } from "../../buttons/ButtonStyles";
import {
  createProjectScheme,
  editProjectScheme,
} from "../../v2/apis/projectsSchemeApi";
import { StartWithDialog } from "./StartWithDialog";
import {
  PROJECT_CURRENCIES,
  PROJECT_MEASUREMENT_UNITS,
} from "../project_management/components/projectDetailFields";
import { ProjectBudgetField } from "../project_management/components/ProjectBudgetField";
import { ProjectLocationField } from "../project_management/components/ProjectLocationField";
import { currencyPatch } from "../project_management/components/projectBudget";
import { AuthContext } from "../../auth/Auth";
import { useLoginState } from "../../studioxr/auth/LoginState";
import { projectDefaultsForProfile } from "../../Home/schedules/profileDefaults";
import { currencyLabel } from "../../libs/currency";
import { uploadFileToGCloud } from "../../libs/firebase";
import { generateID } from "../../libs/util";
import {
  ageDays,
  countOptionalFields,
  diffProjectFields,
  fieldsChangedCsv,
  PROJECT_EVENTS,
  ProjectEntryPoint,
  projectShapeProps,
  trackProjectEvent,
} from "../../libs/projectAnalytics";

const textFieldProps: any = {
  variant: "outlined",
  margin: "normal",
  size: "small",
  fullWidth: true,
  type: "text",
  sx: { paddingRight: "10px", width: "98%", marginTop: "8px" },
};

// 20 MB, matching the cap on the overview's cover upload (P2-2). Without it a
// 60 MB image uploads to GCS in full and is pulled through the CDN on every
// later view.
const MAX_COVER_BYTES = 20 * 1024 * 1024;

// Custom validation function to replace Yup
const validateForm = (values: any) => {
  const errors: any = {};

  if (!values.name) {
    errors.name = "Project name is required";
  } else if (values.name.length < 3) {
    errors.name = "Name must be at least 3 characters";
  }

  return errors;
};

export const ProjectSchemeCreator = ({
  project,
  setShowCreatorModal,
  updateProject,
  // When true, a successful CREATE opens the "What are you starting with?"
  // chooser (#3544 Phase 3). Off by default so associate-flows (FileMenu,
  // AssociatedProjectModal) keep their current behavior.
  showStartWithChooser = false,
  // Analytics label only. Render sites that know where the user came from pass
  // it; the legacy unrouted ones fall back to "direct" rather than guess.
  analyticsEntryPoint = "direct" as ProjectEntryPoint,
}) => {
  const [saving, setSaving] = useState(false);
  const [projectExists, setProjectExists] = useState(false);
  // New-project defaults from the profile (#3593) — only used when creating;
  // an existing project's stored values always win.
  const profile = useLoginState((s) => s.profile);
  const newProjectDefaults = projectDefaultsForProfile(profile);
  const [createdProject, setCreatedProject] = useState<any>(null);
  // Cover image (DS2): picked in the form, uploaded to GCS on submit — only
  // the resulting https URL is stored on the doc.
  const { currentUser } = useContext(AuthContext);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string>("");
  const [coverError, setCoverError] = useState(false);
  const [coverTooLarge, setCoverTooLarge] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const pickCoverFile = (file: File | undefined | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > MAX_COVER_BYTES) {
      setCoverTooLarge(true);
      return;
    }
    setCoverTooLarge(false);
    setCoverFile(file);
    setCoverError(false);
    setCoverPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleClose = () => {
    setShowCreatorModal(false);
  };

  const isEdit = !!project?.id;
  const analyticsBase = {
    entry_point: analyticsEntryPoint,
    surface: "modal" as const,
  };
  // Ref-guarded so React 18 StrictMode's dev double-mount fires this once.
  const openTrackedRef = useRef(false);
  useEffect(() => {
    if (openTrackedRef.current) return;
    openTrackedRef.current = true;
    if (isEdit) {
      trackProjectEvent(PROJECT_EVENTS.editOpened, {
        surface: "card_menu_modal",
        age_days: ageDays(project.createdAt),
        optional_fields_filled: countOptionalFields(project),
      });
    } else {
      trackProjectEvent(PROJECT_EVENTS.createStarted, {
        ...analyticsBase,
        is_onboarding: false,
      });
    }
    // Which mode the modal opened in is fixed for its lifetime.
  }, []);

  useEffect(() => {
    if (project.id) {
      setProjectExists(true);
    } else {
      setProjectExists(false);
    }
  }, [project]);

  const handleSubmit = async (values: any) => {
    setSaving(true);
    setSaveError(false);
    // try/catch so a save failure reaches setSaving(false) and surfaces an error
    // instead of wedging the button on "Saving..." (P2-5).
    try {
      // Omit empty type/measurementUnit — enum-typed, "" violates them (P2-7).
      const clean: any = { ...values };
      if (!clean.type) delete clean.type;
      if (!clean.measurementUnit) delete clean.measurementUnit;
      if (project?.id) {
        // Sanitize the MERGED payload too: the spread below re-introduces a
        // legacy doc's own empty type/measurementUnit. (An empty already in
        // Firestore stays — setDoc(merge:true) won't delete an omitted field.)
        const merged: any = { ...project, ...clean };
        if (!merged.type) delete merged.type;
        if (!merged.measurementUnit) delete merged.measurementUnit;
        // Diff BEFORE the write: editProjectScheme sends the whole document.
        const changedFields = diffProjectFields(project, merged);
        const updatedProject = await editProjectScheme(merged);
        updateProject(updatedProject);
        setShowCreatorModal(false);
        trackProjectEvent(PROJECT_EVENTS.updated, {
          surface: "card_menu_modal",
          fields_changed: fieldsChangedCsv(changedFields),
          fields_changed_count: changedFields.length,
          renamed: changedFields.includes("name"),
          optional_fields_filled_after: countOptionalFields(updatedProject),
          age_days: ageDays(updatedProject.createdAt),
        });
      } else {
        let coverImage: string | undefined;
        if (coverFile && currentUser?.uid) {
          try {
            coverImage = await uploadFileToGCloud(
              `userImages/${currentUser.uid}`,
              `project_cover_${generateID()}`,
              coverFile,
            );
            trackProjectEvent(PROJECT_EVENTS.coverUploaded, {
              surface: "create_modal",
              bytes: coverFile.size,
            });
          } catch (e) {
            // Fail soft: create the project without a cover; it stays editable
            // from the project overview.
            console.error("Project cover upload failed", e);
            setCoverError(true);
          }
        }
        const newProject = await createProjectScheme({
          ...clean,
          ...(coverImage ? { coverImage } : {}),
          id: project.id,
        });
        updateProject(newProject, true);
        trackProjectEvent(PROJECT_EVENTS.created, {
          ...analyticsBase,
          is_onboarding: false,
          ...projectShapeProps(newProject),
        });
        if (showStartWithChooser) {
          setCreatedProject(newProject);
        } else {
          setShowCreatorModal(false);
        }
      }
    } catch (e) {
      console.error("Failed to save project", e);
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  };

  if (createdProject) {
    return (
      <StartWithDialog
        project={createdProject}
        onClose={() => setShowCreatorModal(false)}
      />
    );
  }

  return (
    <Modal
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      open={true}
      onClose={handleClose}
    >
      <Grid
        sx={{
          // Size to content with constraints (fixed width for the slim
          // create form so the cover dropzone has room)
          width: projectExists ? "auto" : 540,
          maxWidth: "90vw",
          maxHeight: "90vh",
          minWidth: "400px",
          margin: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 24,
          overflow: "auto",
          minHeight: "fit-content",
          padding: "10px",
        }}
      >
        <Formik
          initialValues={{
            name: project?.name || "New Project",
            type: project?.type || "",
            phase: project?.phase || "",
            startDate: project?.startDate || "",
            location: project?.location || "",
            description: project?.description || "",
            // Extended metadata (#3544 Phase 3) — all optional
            size: project?.size || "",
            budget: project?.budget || "",
            currency:
              project?.currency ||
              (project?.id ? "" : newProjectDefaults.currency),
            measurementUnit:
              project?.measurementUnit ||
              (project?.id ? "" : newProjectDefaults.measurementUnit),
            estCompletionDate: project?.estCompletionDate || "",
          }}
          validate={validateForm}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting, values, setValues }) => {
            // The structured fields (#3787) must see the same
            // { ...project, ...values } merge handleSubmit saves — bare
            // `values` lacks the stored locationDetails/budgetAmount/
            // budgetRange until edited. One atomic setValues per gesture.
            const merged = { ...project, ...values };
            const applyPatch = (patch: Record<string, any>) =>
              setValues((prev: any) => ({ ...prev, ...patch }));
            return (
              <Form>
                <Stack sx={{ paddingLeft: "10px", paddingRight: "10px" }}>
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                    }}
                  >
                    <DialogTitle
                      sx={{ paddingLeft: "0px", fontWeight: "bold" }}
                    >
                      {!projectExists ? "Create New Project" : "Edit Project"}
                    </DialogTitle>
                  </Grid>

                  {/* Project Name */}
                  <Field
                    as={TextField}
                    {...textFieldProps}
                    id="name"
                    label="Project Name"
                    name="name"
                    placeholder="e.g. Riverside Loft"
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                  />

                  {/* Project Type — DS2 expanded list */}
                  <Field
                    as={TextField}
                    {...textFieldProps}
                    id="type"
                    label="Project Type"
                    name="type"
                    select
                    error={touched.type && !!errors.type}
                    helperText={touched.type && errors.type}
                  >
                    {projectTypeOptions(project?.type).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Field>

                  {!projectExists ? (
                    <>
                      {/* Slim create form (DS2): timeframe, cover image and
                        description. Phase/location/size/budget/currency/
                        measurements stay editable on the project overview's
                        Project Details. */}
                      <Typography
                        sx={{ mt: 1.5, fontSize: 14, fontWeight: 700 }}
                      >
                        Timeframe{" "}
                        <Typography
                          component="span"
                          sx={{ fontSize: 14, color: "#9c9ca1" }}
                        >
                          (Optional)
                        </Typography>
                      </Typography>
                      <Stack direction="row">
                        <Field
                          as={TextField}
                          {...textFieldProps}
                          id="startDate"
                          label="Start date"
                          name="startDate"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                        <Field
                          as={TextField}
                          {...textFieldProps}
                          id="estCompletionDate"
                          label="End date"
                          name="estCompletionDate"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>

                      <Typography
                        sx={{ mt: 1.5, mb: 1, fontSize: 14, fontWeight: 700 }}
                      >
                        Cover image{" "}
                        <Typography
                          component="span"
                          sx={{ fontSize: 14, color: "#9c9ca1" }}
                        >
                          (Optional)
                        </Typography>
                      </Typography>
                      <input
                        ref={coverInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        data-testid="cover-image-input"
                        onChange={(e) => {
                          pickCoverFile(e.target.files?.[0]);
                          // Reset so re-picking the SAME file after a
                          // rejected/failed pick still fires change; the File is
                          // already captured above.
                          e.currentTarget.value = "";
                        }}
                      />
                      <Box
                        onClick={() => coverInputRef.current?.click()}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          pickCoverFile(e.dataTransfer.files?.[0]);
                        }}
                        sx={{
                          border: "1.5px dashed #d4d4d4",
                          borderRadius: 3,
                          backgroundColor: "#fafafa",
                          height: 150,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          p: 2,
                          cursor: "pointer",
                          backgroundImage: coverPreview
                            ? `url(${coverPreview})`
                            : "none",
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          "&:hover": { borderColor: "#b9b2a8" },
                        }}
                      >
                        {!coverPreview && (
                          <Typography
                            sx={{
                              maxWidth: 320,
                              color: "#8c8c8e",
                              fontSize: 14,
                              lineHeight: 1.5,
                            }}
                          >
                            Drag and drop your file here or{" "}
                            <Typography
                              component="span"
                              sx={{
                                color: "#1a1a1a",
                                fontWeight: 700,
                                textDecoration: "underline",
                                fontSize: 14,
                              }}
                            >
                              browse to upload
                            </Typography>
                          </Typography>
                        )}
                      </Box>
                      {coverTooLarge && (
                        <Typography
                          sx={{ mt: 0.5, fontSize: 13, color: "error.main" }}
                        >
                          Cover image must be under 20 MB.
                        </Typography>
                      )}
                      {coverError && (
                        <Typography
                          sx={{ mt: 0.5, fontSize: 13, color: "error.main" }}
                        >
                          Cover upload failed — the project was created without
                          it. You can add one from the project page.
                        </Typography>
                      )}

                      <Field
                        as={TextField}
                        {...textFieldProps}
                        id="description"
                        label="Project description (optional)"
                        name="description"
                        placeholder="Describe your project"
                        multiline
                        rows={4}
                        sx={{ ...textFieldProps.sx, marginTop: "16px" }}
                      />
                    </>
                  ) : (
                    <>
                      {/* Project Phase */}
                      <Field
                        as={TextField}
                        {...textFieldProps}
                        id="phase"
                        label="Project Phase"
                        name="phase"
                        select
                        error={touched.phase && !!errors.phase}
                        helperText={touched.phase && errors.phase}
                      >
                        <MenuItem value="Concept Design">
                          Concept Design
                        </MenuItem>
                        <MenuItem value="Schematic Design">
                          Schematic Design
                        </MenuItem>
                        <MenuItem value="Design Development">
                          Design Development
                        </MenuItem>
                        <MenuItem value="Construction Documents">
                          Construction Documents
                        </MenuItem>
                        <MenuItem value="Construction Administration">
                          Construction Administration
                        </MenuItem>
                      </Field>

                      {/* Target Date */}
                      <Field
                        as={TextField}
                        {...textFieldProps}
                        id="startDate"
                        label="Target Date"
                        name="startDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        error={touched.startDate && !!errors.startDate}
                        helperText={touched.startDate && errors.startDate}
                      />

                      {/* Project Location — structured city autocomplete (#3787) */}
                      <Box sx={textFieldProps.sx}>
                        <ProjectLocationField
                          floatingLabel
                          label="Project Location"
                          id="location"
                          project={merged}
                          onChange={applyPatch}
                        />
                      </Box>

                      {/* Extended metadata (#3544 Phase 3) — optional */}
                      <Stack direction="row">
                        <Field
                          as={TextField}
                          {...textFieldProps}
                          id="size"
                          label="Size (optional)"
                          name="size"
                          placeholder="2,450 sf"
                        />
                        {/* Validated amount / range preset (#3787) */}
                        <Box sx={textFieldProps.sx}>
                          <ProjectBudgetField
                            floatingLabel
                            label="Budget (optional)"
                            id="budget"
                            project={merged}
                            onChange={applyPatch}
                          />
                        </Box>
                      </Stack>
                      <Stack direction="row">
                        <Field
                          as={TextField}
                          {...textFieldProps}
                          id="currency"
                          label="Currency"
                          name="currency"
                          select
                          // currencyPatch also re-renders a structured budget's
                          // display string on a currency switch (#3787).
                          onChange={(e) =>
                            applyPatch(currencyPatch(merged, e.target.value))
                          }
                        >
                          {PROJECT_CURRENCIES.map((currency) => (
                            <MenuItem key={currency} value={currency}>
                              {currencyLabel(currency)}
                            </MenuItem>
                          ))}
                        </Field>
                        <Field
                          as={TextField}
                          {...textFieldProps}
                          id="measurementUnit"
                          label="Measurements"
                          name="measurementUnit"
                          select
                        >
                          {PROJECT_MEASUREMENT_UNITS.map((unit) => (
                            <MenuItem key={unit} value={unit}>
                              {unit}
                            </MenuItem>
                          ))}
                        </Field>
                      </Stack>
                      <Field
                        as={TextField}
                        {...textFieldProps}
                        id="estCompletionDate"
                        label="Est. Completion (optional)"
                        name="estCompletionDate"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                      />

                      {/* Project Description */}
                      <Field
                        as={TextField}
                        {...textFieldProps}
                        id="description"
                        label="Project Description"
                        name="description"
                        multiline
                        rows={6}
                        error={touched.description && !!errors.description}
                        helperText={touched.description && errors.description}
                      />
                    </>
                  )}

                  {/* Action Buttons */}
                  <Grid
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: "20px",
                      marginBottom: "20px",
                      gap: "16px",
                    }}
                  >
                    <ActionButton variant="contained" onClick={handleClose}>
                      Cancel
                    </ActionButton>
                    <ActionButton
                      disabled={isSubmitting || saving}
                      variant="contained"
                      type="submit"
                    >
                      {saving
                        ? "Saving..."
                        : projectExists
                          ? "Save"
                          : "New Project"}
                    </ActionButton>
                  </Grid>
                  {saveError && (
                    <Typography
                      sx={{ color: "error.main", fontSize: 13, mt: -1, mb: 1 }}
                    >
                      Couldn't save the project. Please try again.
                    </Typography>
                  )}
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Grid>
    </Modal>
  );
};
